// VendorAPI.js
import { db } from "../firebase";
import { collection, query, where } from "firebase/firestore";
import {
  getDocs,
  getDoc,
  updateDoc,
  doc,
  setDoc,
} from "firebase/firestore";
import { fetchUsersWithIDs } from "../Events/utils";

class VendorAPI {
  static collectionName = "vendors"; // Static property for the collection name

  static async fetchVendorsByIDs(values) {
    return VendorAPI.fetchDocumentsByPropertyValues("vendorID", values);
  }

  static async fetchAllVendors() {
    const vendorsCollection = collection(db, VendorAPI.collectionName);
    const querySnapshot = await getDocs(vendorsCollection);

    const vendorIDs = querySnapshot.docs
      .map((d) => d.data().vendorID)
      .filter(Boolean);

    // A host's display name is canonical on the user doc (vendorID === uid),
    // not on the vendor doc — so join the users collection to label vendors.
    const users = await fetchUsersWithIDs(db, vendorIDs);
    const nameByID = users.reduce((acc, u) => {
      acc[u.userID] = u.name;
      return acc;
    }, {});

    return vendorIDs.map((vendorID) => ({
      vendorID,
      name: nameByID[vendorID] || "New vendor",
    }));
  }
  // Every vendor doc as { id, data }. The doc id stays apart from the data so a
  // stray `id` field on a doc can't shadow it.
  static async listVendorDocs() {
    const snapshot = await getDocs(collection(db, VendorAPI.collectionName));
    return snapshot.docs.map((d) => ({ id: d.id, data: d.data() }));
  }

  static async fetchDocumentsByPropertyValues(property, values) {
    const MAX_BATCH_SIZE = 10;
    const batches = [];

    for (let i = 0; i < values.length; i += MAX_BATCH_SIZE) {
      const batchValues = values.slice(i, i + MAX_BATCH_SIZE);
      batches.push(VendorAPI.fetchBatch(property, batchValues));
    }

    const results = await Promise.all(batches);
    return results.flat();
  }

  static async fetchBatch(property, values) {
    const q = query(
      collection(db, VendorAPI.collectionName),
      where(property, "in", values)
    );

    const querySnapshot = await getDocs(q);
    const documents = querySnapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));

    return documents;
  }

  // static async getVendorById(vendorID) {
  //   const vendorsCollection = collection(db, VendorAPI.collectionName);
  //   const q = query(vendorsCollection, where("vendorID", "==", vendorID));
  //   const querySnapshot = await getDocs(q);

  //   const vendors = [];
  //   querySnapshot.forEach((doc) => {
  //     vendors.push({ id: doc.id, ...doc.data() });
  //   });

  //   return vendors;
  // }

  static async updateVendor(vendorID, property, value) {
    try {
      // Write by doc id, not a where("vendorID","==") query. Vendor docs
      // provisioned by adminReviewHostApplication have no vendorID field, so the
      // field query returned empty and silently no-op'd (meetupMemberID never
      // saved, host stuck on /profile-setup). The doc is always keyed by uid.
      const vendorRef = doc(db, "vendors", vendorID);
      await updateDoc(vendorRef, { [property]: value });

      const updatedDoc = await getDoc(vendorRef);
      const updatedVendor = updatedDoc.data();

      localStorage.setItem("vendor", JSON.stringify(updatedVendor));
      return updatedVendor;
    } catch (error) {
      console.error("Error updating vendor: ", error);
      throw error;
    }
  }

  static async createVendor(userID) {
    try {
      const vendorDoc = doc(db, "vendors", userID);
      const vendorSnapshot = await getDoc(vendorDoc);

      if (vendorSnapshot.exists()) {
        return vendorSnapshot.data();
      }

      const vendor = { vendorID: userID };
      await setDoc(vendorDoc, vendor);
      return vendor;
    } catch (error) {
      console.error("Error creating vendor: ", error);
      throw error;
    }
  }

  static async getVendorById(vendorID) {
    const vendorDoc = await getDoc(doc(db, "vendors", vendorID));
    return vendorDoc.exists() ? vendorDoc.data() : null;
  }

  static async addVendor(vendor) {
    const vendorRef = doc(db, "vendors", vendor.vendorID);
    await setDoc(vendorRef, vendor);
    return vendor;
  }

  static async updateVendorProfile(vendorID, meetupMemberID, bio) {
    try {
      // Get the vendor document reference (assuming vendorID is the document ID)
      const vendorDocRef = doc(db, "vendors", vendorID);
      const vendorDoc = await getDoc(vendorDocRef);
      const currentData = vendorDoc.data();

      // A host's name/photo are canonical on the user doc, not here — the vendor
      // doc only carries host-specific fields (meetupMemberID, bio, Stripe).
      const updates = {};

      // Only update meetupMemberID if a valid value is provided
      if (meetupMemberID !== undefined && meetupMemberID !== null && meetupMemberID !== "") {
        updates.meetupMemberID = meetupMemberID;
      }

      // Only update bio when an explicit value was passed in
      if (bio !== undefined) {
        updates.bio = bio;
      }

      // Update the document
      await updateDoc(vendorDocRef, updates);

      // Return/cache the FULL vendor doc with the updates applied. Stripping it
      // to a handful of fields would drop e.g. stripeOnboardingComplete from
      // the localStorage copy that GlobalContext serves on the next load.
      const updatedVendor = {
        ...currentData,
        vendorID,
        ...updates,
      };

      localStorage.setItem("vendor", JSON.stringify(updatedVendor));
      return updatedVendor;
    } catch (error) {
      console.error("Error updating vendor profile: ", error);
      throw error;
    }
  }
}

export default VendorAPI;
